/**
 * SLA and Alert Enums
 *
 * Enums for SLA management, alerts, and monitoring.
 * Issue #313 - Centralize enums to shared-types
 *
 * Note: Some monitoring types (ServiceHealthStatus, TestRunStatus, IncidentSeverity)
 * are defined in dtos/monitoring.dto.ts as Zod schemas. These should eventually
 * be migrated to use z.nativeEnum() from this file.
 */

import { z } from 'zod';

// ============================================================================
// SLA STATUS
// ============================================================================

/**
 * SLA compliance status
 */
export enum SLAComplianceStatus {
  /** SLA is on track */
  ON_TRACK = 'ON_TRACK',
  /** SLA is at risk of being breached */
  WARNING = 'WARNING',
  /** SLA has been breached */
  BREACHED = 'BREACHED',
  /** SLA tracking is paused */
  PAUSED = 'PAUSED',
  /** SLA period is completed */
  COMPLETED = 'COMPLETED',
}

/** Zod schema for SLAComplianceStatus validation */
export const SLAComplianceStatusSchema = z.nativeEnum(SLAComplianceStatus);

/** Array of all SLA compliance statuses for iteration */
export const SLA_COMPLIANCE_STATUSES = Object.values(SLAComplianceStatus);

// ============================================================================
// ALERT LEVEL
// ============================================================================

/**
 * Alert severity levels for quota and monitoring
 */
export enum AlertLevel {
  /** Low severity warning */
  WARNING = 'warning',
  /** Critical threshold reached */
  CRITICAL = 'critical',
  /** Limit has been exceeded */
  EXCEEDED = 'exceeded',
}

/** Zod schema for AlertLevel validation */
export const AlertLevelSchema = z.nativeEnum(AlertLevel);

/** Array of all alert levels for iteration */
export const ALERT_LEVELS = Object.values(AlertLevel);

// ============================================================================
// ANOMALY SEVERITY
// ============================================================================

/**
 * Anomaly detection severity
 */
export enum AnomalySeverity {
  CRITICAL = 'critical',
  WARNING = 'warning',
  INFO = 'info',
}

/** Zod schema for AnomalySeverity validation */
export const AnomalySeveritySchema = z.nativeEnum(AnomalySeverity);

// ============================================================================
// BREACH SEVERITY
// ============================================================================

/**
 * SLA breach severity
 */
export enum BreachSeverity {
  MINOR = 'minor',
  MAJOR = 'major',
  CRITICAL = 'critical',
}

/** Zod schema for BreachSeverity validation */
export const BreachSeveritySchema = z.nativeEnum(BreachSeverity);

// ============================================================================
// SERVICE HEALTH STATUS
// ============================================================================

/**
 * Service health status for monitoring
 */
export enum ServiceHealthStatus {
  HEALTHY = 'healthy',
  DEGRADED = 'degraded',
  UNHEALTHY = 'unhealthy',
  UNKNOWN = 'unknown',
}

/** Zod schema for ServiceHealthStatus validation */
export const ServiceHealthStatusSchema = z.nativeEnum(ServiceHealthStatus);

/** Array of all service health statuses for iteration */
export const SERVICE_HEALTH_STATUSES = Object.values(ServiceHealthStatus);

// ============================================================================
// TEST RUN STATUS
// ============================================================================

/**
 * E2E/Canary test run status
 */
export enum TestRunStatus {
  RUNNING = 'RUNNING',
  PASSED = 'PASSED',
  FAILED = 'FAILED',
  TIMEOUT = 'TIMEOUT',
  CANCELLED = 'CANCELLED',
}

/** Zod schema for TestRunStatus validation */
export const TestRunStatusSchema = z.nativeEnum(TestRunStatus);

/** Array of all test run statuses for iteration */
export const TEST_RUN_STATUSES = Object.values(TestRunStatus);

// ============================================================================
// INCIDENT SEVERITY
// ============================================================================

/**
 * Incident severity levels
 */
export enum IncidentSeverity {
  CRITICAL = 'CRITICAL',
  WARNING = 'WARNING',
  INFO = 'INFO',
}

/** Zod schema for IncidentSeverity validation */
export const IncidentSeveritySchema = z.nativeEnum(IncidentSeverity);
